import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/ui/use-toast';

// shadcn UI components
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';

// icons
import { Add, Chart, Profile2User } from 'iconsax-react';

// hooks & services
import { useAuth } from '../hooks/useAuth';
import { getDashboardStats } from '../services/tenantApi';

// ==============================|| DASHBOARD ||============================== //

const Dashboard = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalAgencies: { value: 0, change: '+0%' },
    activeAgencies: { value: 0, change: '+0%' },
    newThisMonth: { value: 0, change: '+0%' }
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await getDashboardStats();
        if (response.success && response.data) {
          setDashboardData(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        toast({ 
          variant: 'destructive', 
          title: 'Error',
          description: error.response?.data?.message || 'Failed to load dashboard metrics' 
        });
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const stats = [
    {
      title: 'Total Agencies',
      value: dashboardData.totalAgencies.value,
      change: dashboardData.totalAgencies.change,
      bgBadge: 'bg-violet-100 text-violet-700 hover:bg-violet-100',
      textColor: 'text-violet-600',
      icon: <Profile2User size={24} />
    },
    {
      title: 'Active Agencies',
      value: dashboardData.activeAgencies.value,
      change: dashboardData.activeAgencies.change,
      bgBadge: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
      textColor: 'text-emerald-600',
      icon: <Chart size={24} />
    },
    {
      title: 'New This Month',
      value: dashboardData.newThisMonth.value,
      change: dashboardData.newThisMonth.change,
      bgBadge: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
      textColor: 'text-blue-600',
      icon: <Add size={24} />
    }
  ];

  const quickActions = [];

  if (hasPermission('client.create')) {
    quickActions.push({
      title: 'Onboard New Agency',
      description: 'Add a new healthcare agency to the platform',
      action: () => navigate('/tenants/create'),
      variant: 'default'
    });
  }

  if (hasPermission('client.read')) {
    quickActions.push({
      title: 'View All Agencies',
      description: 'Manage existing healthcare agencies',
      action: () => navigate('/tenants'),
      variant: 'outline'
    });
  }

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1">
      <h1 className="text-2xl font-bold tracking-tight text-foreground">
        Admin Dashboard
      </h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-3xl font-bold ${stat.textColor}`}>
                    {stat.value}
                  </h2>
                  <p className="text-sm font-medium text-muted-foreground mt-1">
                    {stat.title}
                  </p>
                  <div className="mt-2">
                    <Badge className={`${stat.bgBadge} border-none font-semibold px-2.5 py-0.5`}>
                      {stat.change}
                    </Badge>
                  </div>
                </div>
                <div className={stat.textColor}>
                  {stat.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      {quickActions.length > 0 && (
        <>
          <h2 className="text-xl font-semibold tracking-tight text-foreground mt-8">
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {quickActions.map((action, index) => (
              <Card 
                key={index} 
                className="cursor-pointer hover:border-primary/50 transition-colors duration-200" 
                onClick={action.action}
              >
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-foreground">
                    {action.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1 mb-4">
                    {action.description}
                  </p>
                  <Button variant={action.variant}>
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;